import { CircleButton } from '@web/components/mobile/TopBar'
import { Icon, type IconName } from '@web/components/ui/icons'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { type ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * Bottom sheet in the native style: a grabber, a round close button, the
 * title centred, and content laid out in inset row groups by the caller.
 */
export function Sheet({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  className = '',
  testId,
}: {
  open: boolean
  onClose: () => void
  title: string
  eyebrow?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  testId?: string
}) {
  return open ? (
    <SheetPanel
      onClose={onClose}
      title={title}
      eyebrow={eyebrow}
      footer={footer}
      className={className}
      testId={testId}
    >
      {children}
    </SheetPanel>
  ) : null
}

function SheetPanel({
  onClose,
  title,
  eyebrow,
  children,
  footer,
  className,
  testId,
}: {
  onClose: () => void
  title: string
  eyebrow?: string
  children: ReactNode
  footer?: ReactNode
  className: string
  testId?: string
}) {
  const panel = useRef<HTMLDivElement>(null)
  useDialogFocus(panel, onClose)
  return createPortal(
    <div
      className="m-sheet-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        className={`m-sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
      >
        <div className="m-sheet-grabber" aria-hidden="true" />
        <div className="m-sheet-heading">
          <CircleButton
            icon="close"
            label={`Close ${title}`}
            onClick={onClose}
          />
          <div className="m-sheet-heading-copy">
            {eyebrow && <span className="label">{eyebrow}</span>}
            <h2>{title}</h2>
          </div>
          <span className="m-sheet-heading-spacer" />
        </div>
        <div className="m-sheet-body">{children}</div>
        {footer && <div className="m-sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** An inset group of rows: the rounded list block iOS settings and the Claude app use. */
export function RowGroup({
  children,
  title,
  className = '',
}: {
  children: ReactNode
  title?: string
  className?: string
}) {
  return (
    <div className={`m-rowgroup ${className}`}>
      {title && <div className="m-rowgroup-title">{title}</div>}
      <div className="m-rowgroup-list">{children}</div>
    </div>
  )
}

/** One row: label, optional description, and a value, chevron or check on the right. */
export function Row({
  label,
  description,
  value,
  icon,
  checked,
  chevron,
  disabled,
  destructive,
  onClick,
  right,
  ariaLabel,
}: {
  label: string
  description?: string
  value?: string
  icon?: IconName
  checked?: boolean
  chevron?: boolean
  disabled?: boolean
  destructive?: boolean
  onClick?: () => void
  right?: ReactNode
  ariaLabel?: string
}) {
  const body = (
    <>
      {icon && (
        <span className="m-row-icon">
          <Icon name={icon} size={16} />
        </span>
      )}
      <span className="m-row-copy">
        <span className="m-row-label">{label}</span>
        {description && (
          <span className="m-row-description">{description}</span>
        )}
      </span>
      {value && <span className="m-row-value">{value}</span>}
      {right}
      {checked && (
        <span className="m-row-check">
          <Icon name="check" size={14} />
        </span>
      )}
      {chevron && (
        <span className="m-row-chevron">
          <Icon name="chevronRight" size={14} />
        </span>
      )}
    </>
  )
  const tone = `m-row ${destructive ? 'is-destructive' : ''}`
  return onClick ? (
    <button
      type="button"
      className={tone}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      aria-pressed={checked !== undefined ? checked : undefined}
      onClick={onClick}
    >
      {body}
    </button>
  ) : (
    <div className={tone}>{body}</div>
  )
}
